<template>
  <div class="c-field-settings flex justify-between items-center">
    <div class="max-w-[480px]">
      <p class="font-bold mb-4">{{ title }}</p>
      <p class="text-sm text-neutral-text">
        {{ description }}
      </p>
    </div>
    <BittsTooltip>
      <BittsSelect
        v-model="selectedValue"
        :options
        :id="selectId"
        class="field-settings__selector"
        dropdown-class-name="field-settings__selector-dropdown"
        :placeholder="placeholder"
        :disabled="isDisabled"
        @update:model-value="onValueChange"
      >
        <template #suffix="{ option }">
          <BittsTag v-if="option.default" color="info" size="x-small">
            Default
          </BittsTag>
        </template>
      </BittsSelect>
      <template #title v-if="isDisabled">
        Enable this CRM to map your opportunity and product fields
      </template>
    </BittsTooltip>
  </div>
</template>

<script setup lang="ts">
import { BittsSelect, BittsTag, BittsTooltip } from '@crossbeam/bitts';

import { computed, onMounted, ref, watch } from 'vue';
import { useRoute } from 'vue-router';

import { usePreferredCRMFeed } from '@/composables/usePreferredCRMFeed';
import { MDM_PROPERTIES, MDM_TYPES } from '@/constants/mdm';
import { useFeedsStore, useSourcesStore } from '@/stores';

type VariantType = 'opportunity-owner' | 'product';

const { variant, sourceFieldId, isDisabled } = defineProps<{
  variant: VariantType;
  sourceFieldId: number | null;
  isDisabled?: boolean;
}>();

const emit =
  defineEmits<
    (
      e: 'field-changed',
      payload: { sourceFieldId: number; sourceId?: number },
    ) => void
  >();
const route = useRoute();
const selectedValue = ref<number | null>(null);

const sourcesStore = useSourcesStore();
const feedsStore = useFeedsStore();
const { preferredCRMFeed } = usePreferredCRMFeed();

const isPreferredCRMFeed = computed(
  () => preferredCRMFeed.value?.id === Number(route.params.id),
);

const variantConfig = computed(() => {
  if (variant === 'opportunity-owner') {
    return {
      title: 'Opportunity Owner Field',
      description:
        'Define what field in your CRM defines the opportunity owner',
      placeholder: 'Opportunity Owner',
      selectId: 'opportunity_owner',
      sources: sourcesStore.dealSources,
      mdmType: MDM_TYPES.DEAL,
      defaultMdmProperty: MDM_PROPERTIES.deal.OWNER_ID,
    };
  }
  return {
    title: 'Product Line Type Field',
    description: 'Define what field in your CRM represents the product line',
    placeholder: 'Product Line Type',
    selectId: 'product',
    sources: sourcesStore.productSources,
    mdmType: MDM_TYPES.PRODUCT,
    defaultMdmProperty: MDM_PROPERTIES.product.NAME,
  };
});

const title = computed(() => variantConfig.value.title);
const description = computed(() => variantConfig.value.description);
const placeholder = computed(() => variantConfig.value.placeholder);
const selectId = computed(() => variantConfig.value.selectId);

const options = computed(() => {
  const feedId = preferredCRMFeed.value?.id;
  if (!feedId) return [];

  return variantConfig.value.sources
    .filter((src) => src.feed_id === feedId)
    .flatMap((src) => src.fields)
    .filter((f) => f.pg_data_type === 'text' && f.is_visible)
    .sort((a, b) => a.display_name.localeCompare(b.display_name))
    .map((f) => ({
      mdmProperty: f.mdm_property,
      value: f.id,
      label: f.display_name,
      default: f.mdm_property === variantConfig.value.defaultMdmProperty,
    }));
});

function onValueChange(value: number) {
  selectedValue.value = value;

  const sourceId = variantConfig.value.sources.find(
    (src) => src.feed_id === preferredCRMFeed.value?.id,
  )?.id;

  const option = options.value.find((o) => o.value === value);
  if (option) {
    emit('field-changed', {
      sourceFieldId: value,
      ...(variant === 'product' ? { sourceId } : {}),
    });
  }
}

function setupOptions() {
  if (sourceFieldId) {
    const foundField = options.value.find(
      (item) => item.value === sourceFieldId,
    );
    if (foundField && foundField.value) {
      selectedValue.value = foundField.value;
    }
  } else {
    const defaultField = options.value.find(
      (item) => item.mdmProperty === variantConfig.value.defaultMdmProperty,
    );
    if (defaultField && defaultField.value) {
      selectedValue.value = defaultField.value;
    }
  }
}

onMounted(async () => {
  if (!isPreferredCRMFeed.value) return;
  await sourcesStore.readySync;
  await feedsStore.readySync;
  setupOptions();
});

watch([() => sourceFieldId, preferredCRMFeed, () => isDisabled], () => {
  if (isDisabled || !isPreferredCRMFeed.value) {
    selectedValue.value = null;
  } else {
    setupOptions();
  }
});
</script>

<style lang="pcss" scoped>
@reference "../css/theme.css";
.field-settings__selector {
  @apply w-[240px];
  :deep(.ant-select-selection-item .bitts-tag .ant-tag) {
    @apply mr-0;
  }
  :deep(.ant-select .ant-select-selector) {
    .ant-select-selection-item div {
      @apply w-full;
      div {
        @apply m-auto flex-1;
      }
    }
  }
}
.field-settings__selector-dropdown
  :deep(.ant-select-item-option-selected .bitts-tag .ant-tag) {
  @apply mr-0;
  :deep(.ant-select-item-option-selected) {
    @apply gap-8;
  }
}
</style>
